import React from "react";
import { RotateCcw } from "lucide-react";
import { AGENT_CHECK_IDS, AGENT_GROUPS, CHECK, LEGACY_AGENT_ALIAS } from "../../checks";
import { Pill } from "../common/Pill";
import { AgentResult, StateOfPlayCard } from "../common/AgentResult";
import type { ActionLog } from "../../types";

const agentMeta = (id: string) => CHECK[LEGACY_AGENT_ALIAS[id] || id];

/* Tracker agents accumulate per-workstream state; their structured outputs
   render in the Trackers tab, so Actions only shows their firing log. */
const TRACKER_IDS = new Set([
  "timeline", "issues", "closing", "signature", "diligence", "commitments", "fundsflow",
]);

export interface ActionsPanelProps {
  byAgent: Record<string, ActionLog[]>;
  /** Latest real skip reason per agent check id (live runs stream them). */
  agentSkips?: Record<string, string>;
  reprocs: ActionLog[];
  activeAgents: Set<string>;
  toggleAgent: (id: string) => void;
  readOnly?: boolean;
}

/* The "Actions" tab — the downstream agent roster grouped by category.
   Shows each agent's fired/hidden state and its outputs on this run, plus any
   reprocessing events triggered by material approvals. */
export function ActionsPanel({ byAgent, agentSkips, reprocs, activeAgents, toggleAgent, readOnly }: ActionsPanelProps): JSX.Element {
  /* Only roster checks count — byAgent can carry spine-check entries
     (dual-role outputs, legacy replays) that are not displayed here. */
  const firedCount = AGENT_CHECK_IDS.filter((id) => byAgent[id]).length;
  /* Latest state-of-play briefing gets a dedicated card at the top. */
  const sopActs = byAgent.stateofplay || [];
  const sopLatest = [...sopActs].reverse().find((a) => a.result);
  return (
    <div>
      <div className="up" style={{ marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
        <span>Workstream Agents · the {AGENT_CHECK_IDS.length}-agent roster</span>
        <span
          className="mono tert"
          style={{ fontSize: 11, textTransform: "none", letterSpacing: 0 }}
        >
          {firedCount}/{AGENT_CHECK_IDS.length} fired
        </span>
      </div>
      <div className="tert" style={{ fontSize: 11.5, marginBottom: 10, lineHeight: 1.5 }}>
        Each agent owns a slice of the deal lawyer's work, reading the comprehended workstream.
        Click an agent to {readOnly ? "see its output" : "toggle whether its output is shown on this run"}.
      </div>

      {sopLatest && activeAgents.has("stateofplay") && (
        <StateOfPlayCard result={sopLatest.result} />
      )}

      {reprocs.map((r, i) => (
        <div className="reproc" key={i}>
          <div className="rh">
            <RotateCcw size={14} /> Reprocessing event
          </div>
          <div style={{ fontSize: 12.5, marginBottom: 4 }}>{r.text}</div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
            {r.detail}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
            {(r.agents || []).map((a) => (
              <span
                key={a}
                className="pill"
                style={{ background: "var(--subtle)", color: "var(--muted)" }}
              >
                {agentMeta(a)?.name || a}
              </span>
            ))}
          </div>
        </div>
      ))}

      {AGENT_GROUPS.map(([groupLabel, ids]) => (
        <div key={groupLabel} style={{ marginBottom: 18 }}>
          <div className="up" style={{ marginBottom: 6, opacity: 0.7 }}>
            {groupLabel}
          </div>
          <div className="agroster">
            {ids.map((id) => {
              const fired = !!byAgent[id];
              const on = activeAgents.has(id);
              const dim = !fired;
              return (
                <div key={id}>
                  <div className={"agrow" + (dim ? " dim" : "")}>
                    <div className="anm">
                      <div className="alab">
                        {agentMeta(id)?.name || id}
                        <span className="agslug mono">{agentMeta(id)?.slug || id}</span>
                        {fired && (
                          <Pill color={on ? "--green" : "--low"} label={on ? "fired" : "hidden"} />
                        )}
                      </div>
                      <div className="asub">{agentMeta(id)?.desc || ""}</div>
                    </div>
                    {fired && <span className="acnt">{(byAgent[id] || []).length}</span>}
                    {!fired && (
                      <span
                        className="pill"
                        style={{ background: "var(--subtle)", color: "var(--tert)", flex: "none" }}
                        title={
                          (agentSkips && agentSkips[id]
                            ? `Skipped — ${agentSkips[id]}`
                            : "Did not fire on this corpus.") +
                          (agentMeta(id)?.trigger ? ` Fires when: ${agentMeta(id)!.trigger}.` : "")
                        }
                      >
                        {agentSkips && agentSkips[id] ? "skipped" : "didn't fire"}
                      </span>
                    )}
                    {!readOnly && fired && (
                      <button
                        className="fchip"
                        style={
                          on
                            ? { background: "var(--fg)", color: "#fff", borderColor: "var(--fg)" }
                            : {}
                        }
                        onClick={() => toggleAgent(id)}
                        aria-pressed={on}
                      >
                        {on ? "on" : "off"}
                      </button>
                    )}
                  </div>
                  {!fired && agentSkips && agentSkips[id] && (
                    <div className="aout" style={{ color: "var(--tert)" }}>
                      Skipped — {agentSkips[id]}
                      {agentMeta(id)?.trigger ? (
                        <span className="tert"> · fires when: {agentMeta(id)!.trigger}</span>
                      ) : null}
                    </div>
                  )}
                  {fired &&
                    on &&
                    (byAgent[id] || []).map((a, i) => (
                      <div className="aout" key={i}>
                        {a.text}
                        {a.result && id !== "stateofplay" && !TRACKER_IDS.has(id) ? (
                          <AgentResult id={id} result={a.result} />
                        ) : null}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
